/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { isSameEntity, sortUtteranceEntities } from 'src/luis/projects/testPane/services/endpointPredictionMapper/predictionMapperHelpers';
import {
    EndpointPredictionDetailsSwagger,
    EndpointPredictionSwagger,
    EntityInstanceSwagger,
    EntityInstanceValueSwagger
} from 'src/luis/projects/testPane/types/swagger/EndpointPredictionSwagger';
import {
    EndpointUtteranceResult,
    UtteranceCompositeEntityStore,
    UtteranceEntityWithTextStore,
    UtteranceResultStore
} from 'src/luis/projects/testPane/types/types';
import { EntityLabel } from 'src/luis/projects/utterances/types/Utterance';
import { ModelType } from 'src/luis/types/ModelType';

/**
 * Extracts the predictions for entities and composite entities for an utterance
 * and its multi intent sub predictions from the endpoint api. Returns an array
 * of utterance results, where the first utterance in the array is the main
 * utterance and the rest of the array are the predicted multi intent sub utterances.
 *
 * @param responseObject The response object received from the endpoint.
 * @returns An array of utterance result objects where the first data point in the array
 * is the main utterance and the rest are multi intent sub predictions.
 */
export const endpointSwaggerToUtteranceStore = (apiObject: EndpointPredictionSwagger): UtteranceResultStore => ({
    mainUtterance: {
        utterance: importFromEndpointV3(apiObject.prediction, apiObject.query),
        entities: _getEntitiesFromEndpointApi(apiObject.prediction, apiObject.query),
        composites: _getCompositeEntitiesFromEndpointApi(apiObject.prediction),
        nDepths: _getNDepthEntitiesFromEndpointApi(apiObject.prediction)
    }
});

/**
 * Gets the predicted entities from the endpoint api and matches them with their parent composite entities
 * by inspecting the composite entities returned from this endpoint response.
 *
 * So for each entity predicted in the utterance:
 * - Checks if it is a child of any of the composite entities found in the predictions for this utterance.
 * - If it was found, it is matched with that composite entity.
 * - The corresponding token text for that entity is matched with the entity name.
 *
 * @param endpointApiObject The response object received from the endpoint.
 * @returns An array of utterance entities with their matching token text. Ex: "My name is Omar"
 * would return { entity: "firstName", text: "Omar" }.
 */
const _getEntitiesFromEndpointApi = (prediction: EndpointPredictionDetailsSwagger, query: string): UtteranceEntityWithTextStore[] => {
    const utterance = importFromEndpointV3(prediction, query);
    const composites = _getCompositeEntitiesFromEndpointApi(prediction);
    const parentsAndChildren: UtteranceEntityWithTextStore[] = composites.reduce((a, cE) => a.concat(cE.parent, ...cE.children), []);

    return utterance.predictedEntities
        .filter(e => e.entity.typeId !== ModelType.Simple || e.role?.name)
        .filter(
            predictedEntity => !composites.find(composite => predictedEntity.entity.name === composite.parent.utteranceEntity.entity.name)
        )
        .filter(
            predictedEntity =>
                !parentsAndChildren.find(e =>
                    isSameEntity(e, {
                        isSuppressed: false,
                        utteranceEntity: predictedEntity,
                        text: utterance.text.slice(predictedEntity.startCharIndex, predictedEntity.endCharIndex + 1)
                    })
                )
        )
        .map(uE => ({
            utteranceEntity: uE,
            isSuppressed: false,
            text: utterance.text.slice(uE.startCharIndex, uE.endCharIndex + 1)
        }))
        .sort((a, b) => sortUtteranceEntities(a.utteranceEntity, b.utteranceEntity, true));
};

/**
 * Gets the composite entities from the endpoint api predictions by parsing a specific field that
 * is only returned in the endpoint api.
 *
 * @param endpointApiObject The response object received from the endpoint.
 * @returns An array of composite entities predicted in the utterance along with the matched
 * text for each composite entity.
 */
const _getCompositeEntitiesFromEndpointApi = (prediction: EndpointPredictionDetailsSwagger): UtteranceCompositeEntityStore[] => {
    const entitiesInstances = prediction.entities.$instance;
    const compositeParents = entitiesInstances ? _getCompositeEntityParentsFromEndpointApi(entitiesInstances) : [];

    return compositeParents
        .map(parent => ({
            parent,
            children: _getCompositeEntityChildrenFromEndpointReponse(
                parent,
                prediction.entities[
                    parent.utteranceEntity.role.name ? parent.utteranceEntity.role.name : parent.utteranceEntity.entity.name
                ]
            )
        }))
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, true));
};

/**
 * Builds the hierarchy which means that this function attaches every
 * parent with its own children and the same for the children so it ends
 * up that the hierarchy is built bottom-top.
 *
 * @param swagger The entity swagger which takes the form:
 * {
 *      [entityName]: [{ ... contains the actual data described in the $instance with its hierarchy ... }],
 *      $instance: { ... contains metadata ... }
 * }
 * @param parent the parent to attach the children for.
 */
const getNDepthEntitiesFromSwaggerRec = (swagger: any, parent: UtteranceEntityWithTextStore) => {
    // Get the instance that has the metadata.
    const instance = swagger.$instance;

    // Get all parents in the instance if exists.
    const nDepthParents = instance ? _getNDepthEntityParentsFromEndpointApi(instance) : [];

    // Build the hierarchy for each parent.
    return nDepthParents
        .map(p => ({
            parent: p,
            children: _getNDepthEntityChildrenFromEndpointReponse(p, swagger[p.utteranceEntity.role.name || p.utteranceEntity.entity.name])
        }))
        .filter(
            c =>
                parent.utteranceEntity.startCharIndex <= c.parent.utteranceEntity.startCharIndex &&
                c.parent.utteranceEntity.endCharIndex <= parent.utteranceEntity.endCharIndex
        )
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, true));
};

/**
 * Gets the n-depth entities from the endpoint api predictions by parsing a specific field that
 * is only returned in the endpoint api.
 *
 * @param endpointApiObject The response object received from the endpoint.
 * @returns An array of n-depth entities predicted in the utterance along with the matched
 * text for each n-depth entity.
 */
const _getNDepthEntitiesFromEndpointApi = (prediction: EndpointPredictionDetailsSwagger): UtteranceCompositeEntityStore[] => {
    const entitiesInstances = prediction.entities.$instance;
    const nDepthParents = entitiesInstances ? _getNDepthEntityParentsFromEndpointApi(entitiesInstances) : [];

    return nDepthParents
        .map(parent => {
            // Gets the current parent swagger.
            const parentSwagger = prediction.entities[parent.utteranceEntity.role.name || parent.utteranceEntity.entity.name];

            return {
                parent,
                // Build the hierarchy of the current parent.
                children: parentSwagger.reduce((children, child) => [...children, ...getNDepthEntitiesFromSwaggerRec(child, parent)], [])
            };
        })
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, true));
};

/**
 * Gets the composite entity parents as utterance entities with their text
 * from the entities main object.
 *
 * @param entitiesObject The entities object.
 */
const _getCompositeEntityParentsFromEndpointApi = (entitiesObject: EntityInstanceSwagger): UtteranceEntityWithTextStore[] =>
    Object.keys(entitiesObject).reduce((output, name) => {
        const entityInstances: EntityInstanceValueSwagger[] = entitiesObject[name];
        const isEntityComposite = entityInstances.some(entity => entity.modelTypeId === ModelType.Composite);

        if (isEntityComposite) {
            return output.concat(
                entityInstances.map(instance => ({
                    utteranceEntity: endpointV3EntityToUtterance(instance, name),
                    text: instance.text
                }))
            );
        }

        return output;
    }, []);

/**
 * Gets the n-depth entity parents as utterance entities with their text
 * from the entities main object.
 *
 * @param entitiesObject The entities object.
 */
const _getNDepthEntityParentsFromEndpointApi = (entitiesObject: EntityInstanceSwagger): UtteranceEntityWithTextStore[] =>
    Object.keys(entitiesObject).reduce((output, name) => {
        const entityInstances: EntityInstanceValueSwagger[] = entitiesObject[name];
        const isEntityNDepth = entityInstances.some(entity => entity.modelTypeId === ModelType.Simple);

        if (isEntityNDepth) {
            return output.concat(
                entityInstances.map(instance => ({
                    utteranceEntity: endpointV3EntityToUtterance(instance, name),
                    text: instance.text
                }))
            );
        }

        return output;
    }, []);

/**
 * Gets the children for a given composite parent entity from the
 * entities endpoint response.
 *
 * @param parentToMatch The parent that I want to get the children for.
 * @param parentsData The object of parents to search in.
 * @returns The children of the composite entity given.
 */
const _getCompositeEntityChildrenFromEndpointReponse = (
    parentToMatch: UtteranceEntityWithTextStore,
    parentsData: any[]
): UtteranceCompositeEntityStore[] => {
    const parentsStartIndices = parentsData
        .map(datum => datum.$instance)
        .map(parentInstance => {
            if (!parentInstance) {
                return [];
            }

            const childNames = Object.keys(parentInstance);
            const minStartIndexForEachChild = childNames.map(childName => {
                const children: any[] = parentInstance[childName];
                const childrenStartIndeces = children.map(child => child.startIndex).sort((a, b) => (a > b ? 1 : -1));

                return childrenStartIndeces[0];
            });

            return minStartIndexForEachChild.sort((a, b) => (a > b ? 1 : -1))[0];
        });

    const indexOfMatchedParent = parentsStartIndices.findIndex(
        index => parentToMatch.utteranceEntity.startCharIndex <= index && index <= parentToMatch.utteranceEntity.endCharIndex
    );
    const matchedComposite = parentsData[indexOfMatchedParent];

    if (!matchedComposite || !matchedComposite.$instance) {
        return [];
    }

    const matchedChildrenNames = Object.keys(matchedComposite.$instance);

    return matchedChildrenNames
        .reduce((children: UtteranceCompositeEntityStore[], childName) => {
            const childInstances: any[] = matchedComposite.$instance[childName];

            return children.concat(
                childInstances.map(childInstance => ({
                    parent: {
                        isSuppressed: false,
                        text: childInstance.text,
                        utteranceEntity: endpointV3EntityToUtterance(childInstance, childName)
                    },
                    // This array is empty because the composite entity has only 1 level deep of children.
                    children: []
                }))
            );
        }, [])
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, true));
};

/**
 * Gets the children for a given n-depth parent entity from the
 * entities endpoint response.
 *
 * @param parentToMatch The parent that I want to get the children for.
 * @param parentsData The object of parents to search in.
 * @returns The children of the n-depth entity given.
 */
const _getNDepthEntityChildrenFromEndpointReponse = (
    parentToMatch: UtteranceEntityWithTextStore,
    parentsData: any[]
): UtteranceCompositeEntityStore[] => {
    const parentsStartIndices = parentsData
        .map(datum => datum.$instance)
        .map(parentInstance => {
            if (!parentInstance) {
                return [];
            }

            const childNames = Object.keys(parentInstance);
            const minStartIndexForEachChild = childNames.map(childName => {
                const children: any[] = parentInstance[childName];
                const childrenStartIndeces = children.map(child => child.startIndex).sort((a, b) => (a > b ? 1 : -1));

                return childrenStartIndeces[0];
            });

            return minStartIndexForEachChild.sort((a, b) => (a > b ? 1 : -1))[0];
        });

    const indexOfMatchedParent = parentsStartIndices.findIndex(
        index => parentToMatch.utteranceEntity.startCharIndex <= index && index <= parentToMatch.utteranceEntity.endCharIndex
    );
    const matchedNDepth = parentsData[indexOfMatchedParent];

    if (!matchedNDepth || !matchedNDepth.$instance) {
        return [];
    }

    const matchedChildrenNames = Object.keys(matchedNDepth.$instance);

    return matchedChildrenNames
        .reduce((children: UtteranceCompositeEntityStore[], childName) => {
            const childInstances: any[] = matchedNDepth.$instance[childName];

            return children.concat(
                childInstances.map(childInstance => {
                    const parentSwagger = matchedNDepth[childName];
                    const parent = {
                        isSuppressed: false,
                        text: childInstance.text,
                        utteranceEntity: endpointV3EntityToUtterance(childInstance, childName)
                    };

                    return {
                        parent,
                        children: parentSwagger.reduce(
                            (childChildren: any[], child: any) => [...childChildren, ...getNDepthEntitiesFromSwaggerRec(child, parent)],
                            []
                        )
                    };
                })
            );
        }, [])
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, true));
};

const importFromEndpointV3 = (prediction: EndpointPredictionDetailsSwagger, query: string): EndpointUtteranceResult => {
    const predictedIntents: EndpointUtteranceResult['predictedIntents'] = Object.keys(prediction.intents).map(name => ({
        intent: {
            id: '',
            name,
            typeId: ModelType.Intent,
            labelsCount: null,
            features: null
        },
        score: prediction.intents[name].score
    }));

    const predictedEntities = prediction.entities.$instance
        ? Object.keys(prediction.entities.$instance).reduce((output, name) => {
              const entityInstances: EntityInstanceValueSwagger[] = prediction.entities.$instance[name];
              const utteranceEntities = entityInstances.map(instance => endpointV3EntityToUtterance(instance, name));

              return output.concat(utteranceEntities);
          }, [])
        : [];

    const predictedSentiment: EndpointUtteranceResult['sentiment'] = prediction.sentiment ? prediction.sentiment : null;

    return {
        text: query,
        tokenizedText: [],
        predictedIntents,
        predictedEntities,
        predictedPatterns: [],
        sentiment: predictedSentiment
    };
};

const endpointV3EntityToUtterance = (apiObject: EntityInstanceValueSwagger, entityName: string): EntityLabel => ({
    entity: {
        id: '',
        name: entityName,
        typeId: apiObject.modelTypeId,
        roles: null,
        labelsCount: 0
    },
    role: { name: apiObject.role || '', id: null },
    startTokenIndex: undefined,
    endTokenIndex: undefined,
    startCharIndex: apiObject.startIndex,
    endCharIndex: <number>apiObject.startIndex + <number>apiObject.length - 1
});
